export const MISSING_POLICY = {
    IGNORE: "IGNORE",
    ENTITY_ZERO: "ENTITY_ZERO",
    TOTAL_ZERO: "TOTAL_ZERO",
    DISQUALIFY: "DISQUALIFY",
};

export const SCORING_TYPE = {
    VALUE: "VALUE",
    SET: "SET",
    RANGE: "RANGE",
    AI_MATCH: "AI_MATCH",
};

export const SET_AGGREGATION = {
    SUM_VALUES: "SUM_VALUES",
    MATCH_COUNT: "MATCH_COUNT",
};

export const AI_MATCH_LEVEL = {
    FULL_MATCH: "FULL_MATCH",
    HIGH_MATCH: "HIGH_MATCH",
    PARTIAL_MATCH: "PARTIAL_MATCH",
    LOW_MATCH: "LOW_MATCH",
    NO_MATCH: "NO_MATCH",
    UNKNOWN: "UNKNOWN",
};

export const OVERRIDE_MODE = {
    LOCKED: "LOCKED",
    ALLOW_RECRUITMENT_OVERRIDE: "ALLOW_RECRUITMENT_OVERRIDE",
};

/** Where a criterion's effective config for a recruitment currently comes from. */
export const INHERITANCE_STATE = {
    LOCAL: "local",
    LOCKED: "locked",
    OVERRIDABLE: "overridable",
    OVERRIDDEN: "overridden",
};

export const createEmptyValueRule = () => ({ value: "", points: null, disqualify: false });
export const createEmptyRangeRule = () => ({ min: null, max: null, points: null, disqualify: false });
export const createEmptySetRule = () => ({ value: "", points: null, disqualify: false });
export const createEmptyAiLevelPoints = () => ({ level: AI_MATCH_LEVEL.FULL_MATCH, points: null, disqualify: false });

/**
 * @typedef {Object} ScoringConfig
 * @property {boolean} scoringEnabled
 * @property {number|null} maxScore
 * @property {string} missingPolicy - enum MISSING_POLICY
 * @property {string} scoringType - enum SCORING_TYPE
 * @property {Array} valueRules - used when scoringType === VALUE
 * @property {Array} rangeRules - used when scoringType === RANGE
 * @property {Array} setRules - used when scoringType === SET
 * @property {string} setAggregation - enum SET_AGGREGATION, used when scoringType === SET
 * @property {boolean} capAtMaxScore - used when scoringType === SET
 * @property {string} aiCriterion - used when scoringType === AI_MATCH
 * @property {Array} aiLevels - used when scoringType === AI_MATCH
 */
export const createEmptyScoringConfig = () => ({
    criterionKey: null,
    scoringEnabled: false,
    maxScore: null,
    description: "",
    missingPolicy: MISSING_POLICY.IGNORE,
    scoringType: SCORING_TYPE.VALUE,
    valueRules: [],
    rangeRules: [],
    setRules: [],
    setAggregation: SET_AGGREGATION.SUM_VALUES,
    capAtMaxScore: true,
    aiCriterion: "",
    aiLevels: [],
    // Admin-only (set in the scenario creator, never edited by the client) -- see OVERRIDE_MODE.
    overrideMode: OVERRIDE_MODE.ALLOW_RECRUITMENT_OVERRIDE,
    scoringVersion: 1,
});

/** Sum of maxScore across scoring-enabled criteria. Criteria without a criterion.scoring shape are ignored. */
export function sumMaxScore(criteria) {
    return (criteria ?? []).reduce((total, criterion) => {
        const scoring = criterion?.scoring;
        if (!scoring?.scoringEnabled) return total;
        return total + (Number(scoring.maxScore) || 0);
    }, 0);
}
